import type { FormDefinition } from "~/api";

/**
 * Shared form-versioning helpers. Versions of the same form are identified by a stable
 * FormTemplate identity (`formTemplateGuid`, from #1020); order within a version group is
 * `createdAt → updatedAt → guid`. This mirrors the server's
 * WorkflowService.GetWorkflowFormVersionKey / GetApplicableWorkflowFormDefinitions, and is the
 * single source of truth the workflow viewer, the forms tab, and the designer all use.
 */

type VersionKeyable = Pick<
  FormDefinition,
  "formType" | "formCode" | "guid" | "formTemplateGuid"
>;

/**
 * The version-group key for a form. Forms sharing a key are versions of each other.
 */
export function getFormVersionKey(formDefinition: VersionKeyable): string {
  // The FormTemplate is the version group: versioned forms share one template,
  // code-less forms get one template each.
  if (formDefinition.formTemplateGuid) {
    return formDefinition.formTemplateGuid.replace(/-/g, "").toUpperCase();
  }

  // Legacy fallback for stale cached payloads that predate form templates.
  const trimmedCode = formDefinition.formCode?.trim();
  if (trimmedCode) {
    return `${formDefinition.formType}:${trimmedCode.toUpperCase()}`;
  }

  // No FormCode provided. Use the guid only for uniqueness so no-code forms stay distinct.
  const guidKey = (formDefinition.guid || "").replace(/-/g, "").toUpperCase();
  return `${formDefinition.formType}:GUID:${guidKey}`;
}

/**
 * Orders two versions oldest → newest by createdAt, then updatedAt, then guid.
 * Returns <0 if `left` is older than `right`.
 */
export function compareFormVersions(
  left: FormDefinition,
  right: FormDefinition,
): number {
  const leftCreatedAt = usableTime(left.createdAt);
  const rightCreatedAt = usableTime(right.createdAt);
  // Only order by createdAt when BOTH are real. Legacy rows saved before duplication
  // stamped a timestamp hold DateTimeOffset.MinValue, which would otherwise sort the
  // newest copy as the oldest version; fall through to updatedAt for those.
  if (
    leftCreatedAt !== undefined &&
    rightCreatedAt !== undefined &&
    leftCreatedAt !== rightCreatedAt
  ) {
    return leftCreatedAt - rightCreatedAt;
  }

  const leftUpdatedAt = usableTime(left.updatedAt) ?? 0;
  const rightUpdatedAt = usableTime(right.updatedAt) ?? 0;
  if (leftUpdatedAt !== rightUpdatedAt) {
    return leftUpdatedAt - rightUpdatedAt;
  }

  return (left.guid ?? "").localeCompare(right.guid ?? "");
}

/**
 * Parses a timestamp, treating missing/unparseable/default-DateTime values as absent.
 * `DateTimeOffset.MinValue` (year 1) round-trips as a valid date, so it needs an explicit
 * floor rather than a null check.
 */
function usableTime(value: string | undefined | null): number | undefined {
  if (!value) return undefined;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return undefined;
  // Anything at or before year 1 is an unset default, not a real creation time.
  return parsed <= Date.parse("0002-01-01T00:00:00Z") ? undefined : parsed;
}

/**
 * Groups form definitions by their version key. Each group is ordered oldest → newest.
 */
export function groupByTemplate(
  formDefinitions: FormDefinition[],
): Map<string, FormDefinition[]> {
  const groups = new Map<string, FormDefinition[]>();
  for (const fd of formDefinitions) {
    const key = getFormVersionKey(fd);
    const list = groups.get(key);
    if (list) {
      list.push(fd);
    } else {
      groups.set(key, [fd]);
    }
  }
  for (const list of groups.values()) {
    list.sort(compareFormVersions);
  }
  return groups;
}

/**
 * All versions (oldest → newest) sharing the given form's template.
 */
export function siblingVersionsOf(
  form: Pick<
    FormDefinition,
    "formType" | "formCode" | "guid" | "formTemplateGuid"
  >,
  formDefinitions: FormDefinition[],
): FormDefinition[] {
  const key = getFormVersionKey(form);
  return formDefinitions
    .filter((fd) => getFormVersionKey(fd) === key)
    .sort(compareFormVersions);
}

/**
 * The version immediately older than `form` within its group, or undefined if it is the oldest.
 */
export function previousVersionOf(
  form: FormDefinition,
  formDefinitions: FormDefinition[],
): FormDefinition | undefined {
  const siblings = siblingVersionsOf(form, formDefinitions);
  const index = siblings.findIndex((fd) => fd.guid === form.guid);
  return index > 0 ? siblings[index - 1] : undefined;
}

/**
 * Picks the "active" version within a group — the one a new workflow would instantiate:
 * the explicitly-flagged default, else the newest. `siblings` must be a single version group.
 */
export function activeVersionOf(
  siblings: FormDefinition[],
): FormDefinition | undefined {
  if (siblings.length === 0) return undefined;
  const explicitDefault = siblings.find(
    (fd) => fd.metaInfo?.defaultForFormCode,
  );
  if (explicitDefault) return explicitDefault;
  // newest = last, since siblings are ordered oldest → newest
  return siblings[siblings.length - 1];
}

/**
 * A version family — one entry per version group. `active` is the version shown collapsed
 * (the one a new workflow would use); `versions` holds all versions in the group.
 */
export interface VersionFamily {
  key: string;
  active: FormDefinition;
  versions: FormDefinition[];
}

export interface FormVersionInfo {
  /** 1-based position in the group, oldest = 1. Display only — not persisted. */
  versionNumber: number;
  /** Total versions in the group. */
  versionCount: number;
  /** True if this is the version a new workflow would use. */
  isActive: boolean;
  /** True if explicitly flagged default (metaInfo.defaultForFormCode). */
  isDefault: boolean;
  /** True if this is the newest version in the group. */
  isLatest: boolean;
  /** All versions in the group, oldest → newest. */
  siblings: FormDefinition[];
}

/**
 * Builds per-form version metadata for a set of form definitions, keyed by form guid.
 */
export function buildVersionInfo(
  formDefinitions: FormDefinition[],
): Map<string, FormVersionInfo> {
  const groups = groupByTemplate(formDefinitions);
  const infoByGuid = new Map<string, FormVersionInfo>();

  for (const siblings of groups.values()) {
    const active = activeVersionOf(siblings);
    const latest = siblings[siblings.length - 1];
    siblings.forEach((fd, index) => {
      if (!fd.guid) return;
      infoByGuid.set(fd.guid, {
        versionNumber: index + 1,
        versionCount: siblings.length,
        isActive: active?.guid === fd.guid,
        isDefault: !!fd.metaInfo?.defaultForFormCode,
        isLatest: latest?.guid === fd.guid,
        siblings,
      });
    });
  }

  return infoByGuid;
}
